import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { BaseDialog, TextField, Typography } from "@coolremote/shared";
import styles from "./ExistingUserFlow.module.css";

interface TwoFactorDialogProps {
  open: boolean;
  submitting: boolean;
  errorText?: string;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}

/**
 * 2FA code prompt for the existing-user registration branch. Deliberately
 * built from `@coolremote/shared` only (`BaseDialog` + `TextField`, no
 * `@material-ui`) — see `LoginStep.tsx`'s file-level note on why this flow
 * stays shared-UI-only, unlike `TwoFactorAuthDialog` (the app's normal
 * login/invite flows).
 */
const TwoFactorDialog: React.FC<TwoFactorDialogProps> = (props) => {
  const { open, submitting, errorText, onConfirm, onCancel } = props;
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!open) {
      setCode("");
    }
  }, [open]);

  const handleCodeChange = (value: string) => setCode(value);

  const handleConfirm = () => {
    onConfirm(code);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      title={t`Two-factor authentication`}
      confirmLabel={t`Verify`}
      cancelLabel={t`Cancel`}
      confirmDisabled={!code}
      loading={submitting}
    >
      <p className={styles.main2faDialogContent}>{t`Enter the verification code sent to your account to continue.`}</p>
      <TextField
        label={t`Verification code`}
        name="twoFactorCode"
        fullWidth
        autoComplete="one-time-code"
        value={code}
        onChange={handleCodeChange}
      />
      {errorText && <Typography className={styles.errorBanner}>{errorText}</Typography>}
    </BaseDialog>
  );
};

export default TwoFactorDialog;
